import { useEffect, useState } from "react";
import { Link } from "react-router";
import { useRequest } from "../context/RequestContext";
import { emailRegex } from "../theme/configurationVariables";

const TEAL = "#4abcc9";
const NAVY = "#0d1f2d";

export interface ContactForm { email: string; message: string; }
export interface ContactErrorForm { email: boolean; message: boolean; }

const INFO_ITEMS = [
    { icon: "mail", label: "Correo electrónico", value: "contacto@heydoctor.mx" },
    { icon: "schedule", label: "Tiempo de respuesta", value: "En menos de 24 horas" },
    { icon: "lock", label: "Privacidad", value: "Tu información es 100% confidencial" },
];

export default function ContactanosPage() {
    const { sendRequest } = useRequest();
    const [messageSent, setMessageSent] = useState(false);
    const [contactForm, setContactForm] = useState<ContactForm>({ email: "", message: "" });
    const [contactErrorForm, setContactErrorForm] = useState<ContactErrorForm>({ email: false, message: false });
    const [focused, setFocused] = useState<string | null>(null);

    useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setContactForm({ ...contactForm, [e.target.name]: e.target.value });
        setContactErrorForm({ ...contactErrorForm, [e.target.name]: false });
    };

    const handleSubmit = async () => {
        const { email, message } = contactForm;
        const errors: ContactErrorForm = {
            email: !email || !email.match(emailRegex),
            message: !message,
        };
        setContactErrorForm(errors);
        if (Object.values(errors).some(x => x)) return;

        const response = await sendRequest<string>({
            path: "/api/contactanos",
            body: { correo: email, mensaje: message },
            method: "post",
            loaderText: "Enviando mensaje...",
        });

        if (response.hasError) return;
        setContactForm({ email: "", message: "" });
        setContactErrorForm({ email: false, message: false });
        setMessageSent(true);
    };

    const inputStyle = (name: string, hasError: boolean): React.CSSProperties => ({
        width: "100%",
        fontFamily: "'Manrope', sans-serif",
        fontSize: "0.95rem",
        padding: "14px 16px",
        borderRadius: 12,
        border: `1.5px solid ${hasError ? "#ef4444" : focused === name ? TEAL : "#e5e7eb"}`,
        outline: "none",
        color: NAVY,
        background: "white",
        boxSizing: "border-box",
        transition: "border-color 0.2s",
        boxShadow: focused === name ? `0 0 0 3px rgba(74,188,201,0.15)` : "none",
    });

    if (messageSent) {
        return (
            <div style={{ fontFamily: "'Manrope',sans-serif", minHeight: "70vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "60px 24px", background: "#f8fbff" }}>
                <div style={{ textAlign: "center", maxWidth: 520 }}>
                    <div style={{ width: 80, height: 80, borderRadius: "50%", background: "rgba(74,188,201,0.15)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 28px" }}>
                        <span className="material-symbols-outlined" style={{ fontSize: 42, color: TEAL }}>check_circle</span>
                    </div>
                    <h2 style={{ fontFamily: "'Montserrat',sans-serif", fontSize: "clamp(1.6rem,4vw,2.4rem)", fontWeight: 800, color: NAVY, margin: "0 0 16px" }}>
                        ¡Mensaje enviado!
                    </h2>
                    <p style={{ color: "#6b7280", fontSize: "1rem", lineHeight: 1.7, margin: "0 0 36px" }}>
                        Hemos recibido tu comentario. En breve responderemos al correo electrónico que nos proporcionaste.
                    </p>
                    <Link to="/" style={{ textDecoration: "none" }}>
                        <button style={{ background: TEAL, color: "white", border: "none", padding: "14px 36px", borderRadius: 12, fontFamily: "'Montserrat',sans-serif", fontWeight: 700, fontSize: "0.95rem", cursor: "pointer" }}>
                            Volver al inicio
                        </button>
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div style={{ fontFamily: "'Manrope', sans-serif", overflowX: "hidden" }}>

            {/* ═══ HERO ═══ */}
            <section style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #1a3a4a 60%, #0e4a5a 100%)`, padding: "56px 24px 48px", position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", inset: 0, opacity: 0.07, backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)", backgroundSize: "32px 32px" }} />
                <div style={{ position: "absolute", right: "-80px", top: "-80px", width: 400, height: 400, background: "radial-gradient(circle, rgba(74,188,201,0.22) 0%, transparent 65%)", pointerEvents: "none" }} />
                <div style={{ maxWidth: 1100, margin: "0 auto", position: "relative", zIndex: 2 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
                        <div style={{ width: 36, height: 3, background: TEAL, borderRadius: 2 }} />
                        <span style={{ color: TEAL, fontFamily: "'Montserrat',sans-serif", fontSize: "0.78rem", fontWeight: 700, letterSpacing: 2, textTransform: "uppercase" as const }}>
                            Estamos para ayudarte
                        </span>
                    </div>
                    <h1 style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "clamp(2.2rem, 5vw, 3.6rem)", fontWeight: 900, color: "#ffffff", lineHeight: 1.1, margin: "0 0 16px", letterSpacing: "-1px" }}>
                        Contáctanos
                    </h1>
                    <p style={{ color: "rgba(255,255,255,0.7)", fontSize: "1.05rem", lineHeight: 1.6, maxWidth: 520, margin: 0 }}>
                        Tu opinión es lo más importante para nosotros. Déjanos un mensaje y nos pondremos en contacto a la brevedad posible.
                    </p>
                </div>
            </section>

            {/* ═══ MAIN CONTENT ═══ */}
            <section style={{ background: "#f8fbff", padding: "72px 24px 80px" }}>
                <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", flexWrap: "wrap", gap: 40, alignItems: "flex-start" }}>

                    {/* Left: info */}
                    <div style={{ flex: "0 1 320px" }}>
                        <h2 style={{ fontFamily: "'Montserrat',sans-serif", fontSize: "1.5rem", fontWeight: 800, color: NAVY, margin: "0 0 12px" }}>
                            Información de contacto
                        </h2>
                        <p style={{ color: "#6b7280", fontSize: "0.95rem", lineHeight: 1.7, margin: "0 0 36px" }}>
                            Responderemos tu mensaje lo antes posible. También puedes escribirnos directamente al correo.
                        </p>

                        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                            {INFO_ITEMS.map(item => (
                                <div key={item.label} style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
                                    <div style={{ width: 44, height: 44, borderRadius: 12, background: "rgba(74,188,201,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                        <span className="material-symbols-outlined" style={{ fontSize: 22, color: TEAL }}>{item.icon}</span>
                                    </div>
                                    <div>
                                        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: "0.78rem", fontWeight: 700, color: "#9ca3af", letterSpacing: 1, textTransform: "uppercase" as const, marginBottom: 3 }}>
                                            {item.label}
                                        </div>
                                        <div style={{ fontSize: "0.9rem", fontWeight: 600, color: NAVY }}>
                                            {item.value}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Decorative card */}
                        <div style={{ marginTop: 40, background: `linear-gradient(135deg, ${NAVY} 0%, #1a3a4a 100%)`, borderRadius: 16, padding: "24px 20px", position: "relative", overflow: "hidden" }}>
                            <div style={{ position: "absolute", right: -20, bottom: -20, width: 100, height: 100, borderRadius: "50%", background: "rgba(74,188,201,0.15)" }} />
                            <span className="material-symbols-outlined" style={{ fontSize: 32, color: TEAL, display: "block", marginBottom: 12 }}>favorite</span>
                            <p style={{ color: "rgba(255,255,255,0.85)", fontSize: "0.9rem", lineHeight: 1.6, margin: 0 }}>
                                "Nuestro equipo está comprometido a responderte con calidez y profesionalismo."
                            </p>
                        </div>
                    </div>

                    {/* Right: form */}
                    <div style={{ flex: "1 1 400px" }}>
                        <div style={{ background: "white", borderRadius: 20, padding: "40px 36px", border: "1.5px solid #eef2f7", boxShadow: "0 4px 32px rgba(0,0,0,0.06)" }}>
                            <h3 style={{ fontFamily: "'Montserrat',sans-serif", fontSize: "1.2rem", fontWeight: 700, color: NAVY, margin: "0 0 28px" }}>
                                Envíanos un mensaje
                            </h3>

                            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                                {/* Email */}
                                <div>
                                    <label style={{ display: "block", fontFamily: "'Montserrat',sans-serif", fontSize: "0.8rem", fontWeight: 700, color: contactErrorForm.email ? "#ef4444" : "#374151", marginBottom: 8 }}>
                                        Correo electrónico *
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={contactForm.email}
                                        onChange={handleChange}
                                        onFocus={() => setFocused("email")}
                                        onBlur={() => setFocused(null)}
                                        placeholder="tu@correo.com"
                                        style={inputStyle("email", contactErrorForm.email)}
                                    />
                                    {contactErrorForm.email && (
                                        <span style={{ fontSize: "0.78rem", color: "#ef4444", marginTop: 4, display: "block" }}>Ingresa un correo electrónico válido</span>
                                    )}
                                </div>

                                {/* Message */}
                                <div>
                                    <label style={{ display: "block", fontFamily: "'Montserrat',sans-serif", fontSize: "0.8rem", fontWeight: 700, color: contactErrorForm.message ? "#ef4444" : "#374151", marginBottom: 8 }}>
                                        Mensaje *
                                    </label>
                                    <textarea
                                        name="message"
                                        value={contactForm.message}
                                        onChange={handleChange as any}
                                        onFocus={() => setFocused("message")}
                                        onBlur={() => setFocused(null)}
                                        placeholder="Cuéntanos en qué podemos ayudarte..."
                                        rows={7}
                                        style={{ ...inputStyle("message", contactErrorForm.message), resize: "vertical" as const }}
                                    />
                                    {contactErrorForm.message && (
                                        <span style={{ fontSize: "0.78rem", color: "#ef4444", marginTop: 4, display: "block" }}>El mensaje es obligatorio</span>
                                    )}
                                </div>

                                {/* Submit */}
                                <button
                                    onClick={handleSubmit}
                                    style={{ background: TEAL, color: "white", border: "none", padding: "15px 32px", borderRadius: 12, fontFamily: "'Montserrat',sans-serif", fontWeight: 700, fontSize: "0.95rem", cursor: "pointer", transition: "all 0.2s", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
                                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = "#3daab5"; (e.currentTarget as HTMLElement).style.transform = "translateY(-1px)"; }}
                                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = TEAL; (e.currentTarget as HTMLElement).style.transform = "translateY(0)"; }}
                                >
                                    <span className="material-symbols-outlined" style={{ fontSize: 20 }}>send</span>
                                    Enviar mensaje
                                </button>

                                <p style={{ fontSize: "0.78rem", color: "#9ca3af", margin: 0 }}>* Campos obligatorios</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
